import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Swords, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-[#0d0d0d] border border-zinc-700 focus:border-[#E10600] outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors duration-300 text-sm";

export default function MatchesAdmin({ apps }) {
  const [matches, setMatches] = useState([]);
  const [form, setForm] = useState({ fighter1_user_id: "", fighter2_user_id: "", date: "", venue: "", rounds: 3 });
  const [result, setResult] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/matches").then((r) => setMatches(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const fighterOptions = apps.map((a) => ({ id: a.user_id, label: `${a.full_name} (${a.pro_record})` }));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/matches", { ...form, rounds: Number(form.rounds) });
      toast.success("Match scheduled");
      setForm({ fighter1_user_id: "", fighter2_user_id: "", date: "", venue: "", rounds: 3 });
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const saveResult = async (id) => {
    const r = result[id];
    if (!r?.winner_user_id) { toast.error("Pick a winner or draw"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/matches/${id}/result`, r);
      toast.success("Result saved — records updated");
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this match? Records are NOT reverted automatically.")) return;
    try {
      await api.delete(`/admin/matches/${id}`);
      toast.success("Match deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section data-testid="admin-matches-section">
      <h2 className="font-display text-3xl uppercase text-white mb-6 flex items-center gap-3">
        <Swords className="h-6 w-6 text-[#E10600]" /> Matches
      </h2>

      <form onSubmit={create} className="border border-zinc-800 bg-[#111111] p-6 sm:p-8 mb-6" data-testid="match-create-form">
        <p className="font-mono2 text-[10px] tracking-[0.35em] text-zinc-500 uppercase mb-5">Schedule a Bout</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <select className={inputCls} value={form.fighter1_user_id} onChange={(e) => setForm({ ...form, fighter1_user_id: e.target.value })} required data-testid="match-fighter1-select">
            <option value="" disabled>Fighter 1 (red corner)</option>
            {fighterOptions.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <select className={inputCls} value={form.fighter2_user_id} onChange={(e) => setForm({ ...form, fighter2_user_id: e.target.value })} required data-testid="match-fighter2-select">
            <option value="" disabled>Fighter 2 (blue corner)</option>
            {fighterOptions.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <input className={inputCls} type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="match-date-input" />
          <input className={inputCls} placeholder="Venue — e.g. Talkatora Stadium, New Delhi" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} required data-testid="match-venue-input" />
          <input className={inputCls} type="number" min="1" max="12" value={form.rounds} onChange={(e) => setForm({ ...form, rounds: e.target.value })} required data-testid="match-rounds-input" placeholder="Rounds" />
        </div>
        <button type="submit" disabled={busy} data-testid="match-create-button" className="mt-5 font-cond font-semibold tracking-[0.25em] bg-[#E10600] hover:bg-[#C00500] disabled:opacity-50 text-white px-8 py-3 transition-colors duration-300">
          {busy ? "SCHEDULING..." : "SCHEDULE MATCH"}
        </button>
      </form>

      {matches.length === 0 ? (
        <p className="font-mono2 text-xs tracking-[0.3em] text-zinc-500 uppercase border border-zinc-800 p-8 text-center" data-testid="matches-admin-empty">No matches scheduled</p>
      ) : (
        <div className="space-y-3" data-testid="admin-matches-list">
          {matches.map((m) => (
            <div key={m.id} className="border border-zinc-800 bg-[#111111] p-5" data-testid={`admin-match-${m.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-cond font-bold text-xl uppercase text-white">
                    {m.fighter1_name} <span className="text-[#E10600]">vs</span> {m.fighter2_name}
                  </p>
                  <p className="font-mono2 text-[10px] tracking-[0.15em] text-zinc-500 mt-1 uppercase">
                    {m.date ? new Date(m.date).toLocaleString("en-IN") : ""} · {m.venue} · {m.rounds} rounds
                  </p>
                  {m.status === "completed" && (
                    <p className="font-mono2 text-[10px] tracking-[0.2em] text-green-500 uppercase mt-2" data-testid={`match-completed-${m.id}`}>
                      {m.winner_user_id === "draw" ? "DRAW" : `WINNER — ${m.winner_user_id === m.fighter1_user_id ? m.fighter1_name : m.fighter2_name}`}
                      {m.method ? ` · ${m.method}` : ""}{m.score ? ` · ${m.score}` : ""}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-mono2 text-[9px] tracking-[0.25em] uppercase border px-2.5 py-1 ${m.status === "scheduled" ? "text-yellow-500 border-yellow-800/60" : "text-green-500 border-green-800/60"}`} data-testid={`match-status-${m.id}`}>
                    {m.status}
                  </span>
                  <button onClick={() => remove(m.id)} data-testid={`match-delete-${m.id}`} className="text-zinc-500 hover:text-red-500 transition-colors" aria-label="Delete match">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {m.status === "scheduled" && (
                <div className="mt-4 pt-4 border-t border-zinc-800/80 grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <select
                    className={inputCls}
                    value={result[m.id]?.winner_user_id || ""}
                    onChange={(e) => setResult({ ...result, [m.id]: { ...result[m.id], winner_user_id: e.target.value } })}
                    data-testid={`match-winner-select-${m.id}`}
                  >
                    <option value="" disabled>Result: winner</option>
                    <option value={m.fighter1_user_id}>{m.fighter1_name}</option>
                    <option value={m.fighter2_user_id}>{m.fighter2_name}</option>
                    <option value="draw">Draw</option>
                  </select>
                  <input className={inputCls} placeholder="Method — KO / Decision / TKO" value={result[m.id]?.method || ""} onChange={(e) => setResult({ ...result, [m.id]: { ...result[m.id], method: e.target.value } })} data-testid={`match-method-input-${m.id}`} />
                  <input className={inputCls} placeholder="Score — e.g. R3 1:42 or 29-28" value={result[m.id]?.score || ""} onChange={(e) => setResult({ ...result, [m.id]: { ...result[m.id], score: e.target.value } })} data-testid={`match-score-input-${m.id}`} />
                  <button onClick={() => saveResult(m.id)} disabled={busy} data-testid={`match-result-save-${m.id}`} className="font-cond font-semibold tracking-[0.2em] bg-[#E10600] hover:bg-[#C00500] disabled:opacity-50 text-white px-6 py-3 transition-colors">
                    SAVE RESULT
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
